import AddIcon from '@mui/icons-material/Add';
import { Grid, IconButton, Box, Tooltip } from '@mui/material';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { IJobBoard } from 'wisestep-api';
import { WsIconEdit, WsIconDelete, WsButton, WsDataTable } from 'wisestep-base';

import {
  useJobBoardAccessControl,
  PremiumJobBoardsAction,
} from '../../../../hooks/useJobBoardAccessControl';
import CWjobsIcon from '../../../../images/jobBoardsIcons/CWJob.png';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { getAccountCount } from '../../../jobs/publish/components/helper/jobBoardHelper';
import JobBoardSetting from '../../JobBoardSetting';
import AccountSettings from '../accountSettings';
import DeleteAccount from '../accountSettings/DeleteAccount';
import { JobBoardDialogHeader } from '../freeJobBoardSetting/FreeJobBoardSetting';
import { accountLimitMessage } from '../helper/jobBoardSettingsHelper';
import useJobBoardSettings from '../hooks/useJobBoardSettings';

import CWJobsPostingAdd from './CWJobsPostingAdd';

interface IcwJobsPostingJobBoardState {
  selectedAccount?: IJobBoard;
  editAccount: boolean;
  openDeleteAccountPopup: boolean;
}
const CWJobsPostingJobBoard = () => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [cwJobsPostingJobBoardState, setCwJobsPostingJobBoardState] =
    useState<IcwJobsPostingJobBoardState>({
      editAccount: false,
      openDeleteAccountPopup: false,
    });
  const jobBoardResponse = useJobBoardSettings();
  const { access, owns } = useJobBoardAccessControl([
    PremiumJobBoardsAction.VIEW_PREMIUM_JOB_POSTING_ACCOUNTS,
    PremiumJobBoardsAction.MANAGE_PREMIUM_JOB_POSTING_CREDITS,
    PremiumJobBoardsAction.DELETE_PREMIUM_JOB_POSTING_ACCOUNTS,
  ]);
  const [view, manage, deleteJobBoard] = access;

  const closeDeleteAccountPopup = () => {
    setCwJobsPostingJobBoardState((prev) => ({
      ...prev,
      openDeleteAccountPopup: false,
    }));
    navigate('/job-boards/cwJobs-jobPosting');
    window.location.reload();
  };

  const handleDeleteAccount = useCallback(
    (index: number) => {
      setCwJobsPostingJobBoardState((prev) => ({
        ...prev,
        selectedAccount: jobBoardResponse.CWJobs[index] ?? '',
      }));
      setCwJobsPostingJobBoardState((prev) => ({
        ...prev,
        openDeleteAccountPopup: true,
      }));
    },
    [jobBoardResponse.CWJobs]
  );

  const handleAddAccount = () => {
    if (getAccountCount(jobBoardResponse) < 10) {
      navigate('/job-boards/cwJobs-jobposting-add');
    } else {
      setNotification(accountLimitMessage());
    }
  };

  const handleEditAccount = useCallback(
    (index: number) => {
      setCwJobsPostingJobBoardState((prev) => ({
        ...prev,
        selectedAccount: jobBoardResponse.CWJobs[index] ?? '',
      }));
      setCwJobsPostingJobBoardState((prev) => ({
        ...prev,
        editAccount: true,
      }));
    },
    [jobBoardResponse.CWJobs]
  );

  const data = useMemo(
    () =>
      jobBoardResponse &&
      jobBoardResponse.CWJobs.map((account: IJobBoard) => ({
        accountName: account.username,
        createdBy: account.createdBy,
        sharedWith: account.jobBoardSharedUsers?.length ?? 0,
        quota: `${account.jobBoardUsage?.postingsConsumed ?? -1} / ${
          account.jobBoardUsage?.quota ?? -1
        }`,
      })),
    [jobBoardResponse]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Account Name',
        accessor: 'accountName',
      },
      {
        Header: 'Created By',
        accessor: 'createdBy',
      },
      {
        Header: 'Shared with',
        accessor: 'sharedWith',
      },
      {
        Header: 'Quota',
        accessor: 'quota',
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: (cell: Cell) => (
          <Grid>
            <Grid spacing={2} direction="row">
              {(owns || manage || view) && (
                <Tooltip title="Edit Account">
                  <IconButton onClick={() => handleEditAccount(cell.row.index)}>
                    <WsIconEdit />
                  </IconButton>
                </Tooltip>
              )}
              {(owns || deleteJobBoard) && (
                <Tooltip title="Delete Account">
                  <IconButton onClick={() => handleDeleteAccount(cell.row.index)}>
                    <WsIconDelete />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
          </Grid>
        ),
      },
    ],
    [deleteJobBoard, handleDeleteAccount, handleEditAccount, manage, owns, view]
  );

  return (
    <JobBoardSetting>
      <Box borderRadius={1.5}>
        {jobBoardResponse?.CWJobs?.length > 0 ? (
          cwJobsPostingJobBoardState.editAccount && cwJobsPostingJobBoardState.selectedAccount ? (
            <AccountSettings
              jobBoardName="CWJobs"
              selectedAccount={cwJobsPostingJobBoardState.selectedAccount}
              callBackHandler={(data) =>
                setCwJobsPostingJobBoardState((prev) => ({
                  ...prev,
                  selectedAccount: { ...data },
                }))
              }
            />
          ) : (
            <Box>
              <JobBoardDialogHeader>
                <Grid container>
                  <Grid item container xs={10} columnGap={2}>
                    <img src={CWjobsIcon} alt="CW Jobs" height="35" />
                  </Grid>
                  {(owns || manage) && (
                    <Grid item xs={2} container justifyContent="flex-end">
                      <WsButton startIcon={<AddIcon fontSize="small" />} onClick={handleAddAccount}>
                        Add account
                      </WsButton>
                    </Grid>
                  )}
                </Grid>
              </JobBoardDialogHeader>
              <WsDataTable
                columns={columns}
                data={data ?? []}
                selectable={false}
                boldItems={['accountName']}
                dense={false}
                headless={false}
                pageable={true}
              />
              {cwJobsPostingJobBoardState.selectedAccount?.id && (
                <DeleteAccount
                  selectedAccountName={cwJobsPostingJobBoardState.selectedAccount?.username ?? ''}
                  selectedAccountId={cwJobsPostingJobBoardState.selectedAccount?.id}
                  selectedAccountPassword={cwJobsPostingJobBoardState.selectedAccount?.password}
                  openDeleteAccountPopup={cwJobsPostingJobBoardState.openDeleteAccountPopup}
                  closeDeleteAccountPopup={closeDeleteAccountPopup}
                />
              )}
            </Box>
          )
        ) : (
          <CWJobsPostingAdd />
        )}
      </Box>
    </JobBoardSetting>
  );
};
export default CWJobsPostingJobBoard;
